import React, { ReactElement, useEffect } from 'react';
import classNames from 'classnames/bind';
import { redirect } from '../../utils/cartUtils';
import { sendEventGA } from '../../utils/gaEvents';
import Icon from '../../atoms/Icon';
import Text from '../../atoms/Text';
import LinkStyle from './QuickLink.pcss';
import { getProductItemPDP, gtmEventPush, segmentEventPush } from '../../../modules/ga/GTMdatalayer';
import screens from '../../constants/luxeScreenType';
import properties from '../../../modules/ajio-util/properties';
import { isMarketing } from '../../../utils/pdp';

const imageBaseUrl = properties.getProperty('imageBaseUrl');

// Define Type
interface LinkPropsType {
  data: LinkArray[];
  header: string;
  eventPush: (A: Record<string, string>) => void;
  luxe?: boolean | undefined;
  renderAsSuggestedLinks?: boolean | undefined;
  productDetails?: any;
}
interface LinkArray {
  link: string;
  text: string;
}

// Bind Pcss Classes
const cx = classNames.bind(LinkStyle);

// Build Component
const getLinkList = (
  luxe: boolean | undefined,
  data: LinkArray[],
  eventPush: (A: Record<string, string>) => void,
  renderAsSuggestedLinks: boolean | undefined,
  productDetails?: any,
): JSX.Element[] => {
  return data.map(
    (item: LinkArray, index: number): JSX.Element => {
      const { link = '', text = '' } = item;
      const redirectFunction = redirect(link);
      const {
        defaultStore,
        pdp: { screenName = '', screenType = '' }
      } = screens;
      return (
        <li
          key={`link-item-${index}`}
          id="link-item"
          className={renderAsSuggestedLinks ? cx('suggestedLinkItem') : cx('LinkItem')}
          onClick={() => {
            const isExternalLink = isMarketing(window.location.href);
            if(isExternalLink && !luxe)
              {
                sessionStorage.setItem('exitIntent', 'true');
              }
            if (luxe) {
              eventPush({
                eventName: 'pdp_view_all_product_click',
                event_action: 'shop more clicks',
                event_label: text
              });
            } else if (!luxe && renderAsSuggestedLinks) {
              // eventPush({ eventName: 'quick_link_click', click_text: text, click_url: link,isViewProductDetails:'no' });
              gtmEventPush({
                event: 'product_details_interactions',
                event_category: 'product details interactions',
                event_action: 'pdp similar links suggested links',
                event_label: text,
                page_name: screenName,
                page_type: screenType,
                store_type: defaultStore
              });
            } else {
              gtmEventPush({
                event: 'pdp_view_all_product_click',
                event_category: 'product details interactions',
                event_action: 'shop more clicks',
                event_label: text,
                page_name: screenName,
                page_type: screenType,
                store_type: defaultStore
              });
            }
            sendEventGA({
              eventCategory: 'Page Interaction',
              eventAction: link,
              eventLabel: text
            });

            segmentEventPush({
              event:'Suggested Links Clicked',
              screen_name: screenName,
              screen_source: screenType,
               products:[{
                ...getProductItemPDP(productDetails)
              }]  
            });

            redirectFunction();
          }}     
        >
          <Text color={luxe ? 'Grey_88' : 'Pelorous'} variant={luxe ? 'T4' : 'T2'} weight="bold" role='link' ariaLabel={`View ${text}`} tabIndex={0}>
            {text}
          </Text>
          {renderAsSuggestedLinks ? (
            <img
              className={cx('link-icon-right')}
              src={`${imageBaseUrl}/static/img/link-right.svg`} alt='Right arrow'
              aria-hidden="true"
            />
          ) : (
            <Icon
              ariaHidden={true}
              icon={luxe ? 'luxe-Go' : 'icon-Chevron-Right'}
              styles={{ color: luxe ? '#e0e0e0' : '#347EA4',padding: '8px 8px', margin:'-6px 0' }} ariaLabel='View more products'
            />
          )}
        </li>
      );
    }
  );
};
const QuickLink = (props: LinkPropsType): ReactElement | null => {
  const { data = [], header = '', eventPush, luxe, renderAsSuggestedLinks, productDetails } = props;

  useEffect(() => {

      const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
            segmentEventPush({
              event:'Suggested Links Viewed',              
              link_description: data,
              screen_name: 'Pdp Screen',    
              products:[{
                ...getProductItemPDP(productDetails)
              }]   
            });
        }
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.1
      }
    );
         
    var quickLinkEle = document.getElementById('more-links');
    if(quickLinkEle){
      observer.observe(quickLinkEle);       
    }

  }, [data]);

  const HeaderNode = header ? (
    <div className={cx('QuickLinkHeader')}>
      <Text variant="T4" weight="bold" color="Nero" textTransform="is-capitalized" role='heading' ariaLevel={2} id='more-products-unique-id'>
        {header}
      </Text>
    </div>
  ) : null;
  const QuickLinkElement = data.length ? (
    <div id="more-links" className={cx('QuickLink')}>
      {luxe ? null : HeaderNode}
      <ul
        className={
          luxe
            ? cx('luxeLinkContainer')
            : renderAsSuggestedLinks
            ? cx('suggestedLinkContainer')
            : cx('LinkContainer')
        }
        aria-labelledby='more-products-unique-id'
      >
              
          {getLinkList(luxe, data, eventPush, renderAsSuggestedLinks, productDetails)}
        </ul>
    </div>
  ) : null;
  return QuickLinkElement;
};

export default QuickLink;
